import {
  AfterViewInit,
  Component,
  ElementRef,
  Input,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { load as cargarYaml } from 'js-yaml';
import type { SwaggerUIBundle as TipoSwaggerUIBundle } from 'swagger-ui-dist';

/**
 * Visor Swagger UI de una especificación OpenAPI que YA se bajó con el token
 * de Cognito (`GET …/documentacion/openapi`). Recibe el YAML como texto, lo
 * parsea con `js-yaml` y lo pinta con `swagger-ui-dist`.
 *
 * - «Try it out» va apagado (`supportedSubmitMethods: []`): la API firmada no
 *   se puede llamar desde el navegador (cabeceras `X-Aguacate-*` fuera de CORS).
 * - El bundle (1,6 MB) y su CSS se cargan sólo cuando este componente se usa:
 *   `import()` diferido, y el CSS viene como estilo del componente con
 *   `ViewEncapsulation.None` porque Swagger crea su DOM fuera de Angular (con
 *   encapsulación emulada no lo alcanzaría). El CSS de Swagger está acotado a
 *   `.swagger-ui`, así que no pisa el resto del panel.
 */
@Component({
  selector: 'app-swagger-viewer',
  templateUrl: './swagger-viewer.component.html',
  styleUrls: ['../../../../../node_modules/swagger-ui-dist/swagger-ui.css', './swagger-viewer.component.sass'],
  encapsulation: ViewEncapsulation.None,
})
export class SwaggerViewerComponent implements AfterViewInit, OnChanges, OnDestroy {
  /** El OpenAPI en YAML (o JSON: `js-yaml` también lo lee). `null` = nada que mostrar. */
  @Input() yaml: string | null = null;
  /** Texto mientras el padre baja el archivo. */
  @Input() cargando = false;
  /** Error del padre (404, red…): se muestra en lugar del visor. */
  @Input() error = '';

  @ViewChild('nodo', { static: true }) nodo!: ElementRef<HTMLDivElement>;

  /** Error propio: YAML inválido o el bundle no cargó. */
  errorVisor = '';
  renderizando = false;

  private listo = false;
  private destruido = false;
  /** Para descartar un render viejo si el YAML cambió mientras cargaba el bundle. */
  private version = 0;

  ngAfterViewInit(): void {
    this.listo = true;
    void this.renderizar();
  }

  ngOnChanges(cambios: SimpleChanges): void {
    if (cambios['yaml'] && this.listo) void this.renderizar();
  }

  ngOnDestroy(): void {
    this.destruido = true;
    this.limpiar();
  }

  private limpiar(): void {
    const el = this.nodo?.nativeElement;
    if (el) el.innerHTML = '';
  }

  private async renderizar(): Promise<void> {
    const version = ++this.version;
    this.errorVisor = '';
    this.limpiar();
    const texto = (this.yaml ?? '').trim();
    if (!texto) return;

    let spec: Record<string, unknown>;
    try {
      const parseado = cargarYaml(texto);
      if (!parseado || typeof parseado !== 'object' || Array.isArray(parseado)) {
        throw new Error('El archivo no es un documento OpenAPI.');
      }
      spec = parseado as Record<string, unknown>;
    } catch (e) {
      this.errorVisor = `No se pudo leer el OpenAPI: ${mensajeDe(e)}`;
      return;
    }

    this.renderizando = true;
    try {
      const SwaggerUIBundle = await cargarBundle();
      if (this.destruido || version !== this.version) return;
      SwaggerUIBundle({
        domNode: this.nodo.nativeElement,
        spec,
        presets: [SwaggerUIBundle['presets']['apis']],
        plugins: [],
        layout: 'BaseLayout',
        deepLinking: false,
        docExpansion: 'list',
        defaultModelsExpandDepth: 1,
        defaultModelExpandDepth: 2,
        displayRequestDuration: false,
        showExtensions: false,
        showCommonExtensions: true,
        // La API firmada no se puede probar desde el navegador: sin «Try it out».
        supportedSubmitMethods: [],
        tryItOutEnabled: false,
      });
    } catch (e) {
      if (version === this.version) this.errorVisor = `No se pudo cargar el visor: ${mensajeDe(e)}`;
    } finally {
      if (version === this.version) this.renderizando = false;
    }
  }
}

/** El bundle se baja una sola vez por sesión (webpack lo deja en un chunk aparte). */
let bundlePromesa: Promise<TipoSwaggerUIBundle> | null = null;

function cargarBundle(): Promise<TipoSwaggerUIBundle> {
  if (!bundlePromesa) {
    bundlePromesa = import('swagger-ui-dist/swagger-ui-bundle').then((m) => {
      // UMD bajo webpack: la función viene en `default`; se tolera el namespace pelado.
      const modulo = m as unknown as { default?: unknown; SwaggerUIBundle?: unknown };
      const fn = modulo.default ?? modulo.SwaggerUIBundle ?? m;
      if (typeof fn !== 'function') throw new Error('bundle inválido');
      return fn as unknown as TipoSwaggerUIBundle;
    });
    bundlePromesa.catch(() => (bundlePromesa = null));
  }
  return bundlePromesa;
}

function mensajeDe(e: unknown): string {
  const m = (e as { message?: unknown })?.message;
  return typeof m === 'string' && m.trim() ? m : 'error desconocido';
}
